/**
 * Centralised SEO copy for the public marketing site `(www)`.
 *
 * Each page gets a localised <title> and <meta description> in EN/ES/DE.
 * The default language for the SSG-rendered HTML is Spanish (`es`) because
 * STILGIA's primary market is Spain. Client-side, the layout auto-switches
 * based on the browser language, but the HTML that Google crawls first is ES.
 *
 * Keep titles ≤ 60 characters and descriptions ≤ 158 characters for optimal
 * Google SERP rendering.
 */

export type WwwLocale = 'es' | 'en' | 'de';
export type WwwPage = 'home' | 'features' | 'pricing' | 'privacy' | 'terms' | 'help' | 'contact' | 'download' | 'shop';

export const SITE_URL = 'https://stilgia.com';
export const SITE_NAME = 'STILGIA';
export const SITE_TWITTER = ''; // not yet on Twitter/X — leave empty
export const DEFAULT_OG_IMAGE = `${SITE_URL}/og-default.png`; // fallback (asset to upload)
export const SUPPORT_EMAIL = 'support@stilgia.com';

// Official store listings — both verified publicly live on 2026-09 (HTTP 200, no account).
export const APP_STORE_URL = 'https://apps.apple.com/de/app/stilgia/id6785586581';
export const GOOGLE_PLAY_URL = 'https://play.google.com/store/apps/details?id=com.mirrorfit.styleai';

type Copy = Record<WwwPage, { title: string; description: string }>;

const COPY: Record<WwwLocale, Copy> = {
  es: {
    home: {
      title: 'STILGIA — Tu armario digital con IA',
      description: 'Organiza tu armario, recibe outfits con IA y pruébate la ropa virtualmente. Ahora con Travel: planifica tus looks y tu maleta inteligente.',
    },
    features: {
      title: 'Funcionalidades — STILGIA',
      description: 'Armario digital con IA, pruebas virtuales, estilista personal, calendario, clima y Travel: planificador de viajes con maleta inteligente.',
    },
    pricing: {
      title: 'Planes y precios — STILGIA',
      description: 'Plan Free permanente de 0 €, Premium y Premium Plus. Sin permanencia. Cancela en cualquier momento desde tu tienda de aplicaciones.',
    },
    privacy: {
      title: 'Política de Privacidad — STILGIA',
      description: 'Cómo STILGIA recopila, usa y protege tus datos personales. Cumplimos con el GDPR y solo guardamos lo estrictamente necesario.',
    },
    terms: {
      title: 'Términos del Servicio — STILGIA',
      description: 'Condiciones de uso de STILGIA, política de suscripciones, derecho de desistimiento y limitaciones de responsabilidad.',
    },
    help: {
      title: 'Centro de ayuda — STILGIA',
      description: 'Preguntas frecuentes sobre STILGIA: cómo crear tu avatar, subir prendas, generar outfits, gestionar tu suscripción y más.',
    },
    download: {
      title: 'Descargar STILGIA — App Store y Google Play',
      description: 'Descarga STILGIA gratis: tu armario digital con IA, pruebas virtuales y outfits. Disponible en App Store y Google Play.',
    },
    shop: {
      title: 'STILGIA Shop — catálogo con prueba virtual',
      description: 'Catálogo seleccionado de marcas colaboradoras con prueba virtual sobre tu avatar. Compra en shop.stilgia.com (Shopify). Envíos solo en Alemania.',
    },
    contact: {
      title: 'Contacto — STILGIA',
      description: `¿Necesitas ayuda? Escríbenos a ${SUPPORT_EMAIL}. Respondemos en menos de 24 horas en días laborables.`,
    },
  },
  en: {
    home: {
      title: 'STILGIA — Your AI Digital Wardrobe',
      description: 'Organise your wardrobe, get AI outfits and try clothes on virtually. Now with Travel: plan your looks and your smart suitcase.',
    },
    features: {
      title: 'Features — STILGIA',
      description: 'AI digital wardrobe, virtual try-ons, personal stylist, outfit calendar, weather-aware picks and Travel: trip planner with smart packing.',
    },
    pricing: {
      title: 'Pricing — STILGIA',
      description: 'Permanent €0 Free plan, Premium and Premium Plus. No lock-in. Cancel any time from your app store.',
    },
    privacy: {
      title: 'Privacy Policy — STILGIA',
      description: 'How STILGIA collects, uses and protects your personal data. GDPR-compliant and we only store what we strictly need.',
    },
    terms: {
      title: 'Terms of Service — STILGIA',
      description: 'STILGIA terms of use, subscription policy, right of withdrawal and limitations of liability.',
    },
    help: {
      title: 'Help Center — STILGIA',
      description: 'Frequently asked questions about STILGIA: how to create your avatar, upload clothes, generate outfits, manage your subscription and more.',
    },
    download: {
      title: 'Download STILGIA — App Store & Google Play',
      description: 'Download STILGIA for free: your AI digital wardrobe, virtual try-ons and outfits. Available on the App Store and Google Play.',
    },
    shop: {
      title: 'STILGIA Shop — catalogue with virtual try-on',
      description: 'Curated catalogue from partner brands with virtual try-on on your avatar. Checkout at shop.stilgia.com (Shopify). Shipping in Germany only.',
    },
    contact: {
      title: 'Contact — STILGIA',
      description: `Need help? Email us at ${SUPPORT_EMAIL}. We reply within 24 hours on business days.`,
    },
  },
  de: {
    home: {
      title: 'STILGIA — Dein KI-gestützter digitaler Kleiderschrank',
      description: 'Organisiere deinen Kleiderschrank, erhalte KI-Outfits und probiere Kleidung virtuell an. Neu: Travel — plane deine Looks und deinen smarten Koffer.',
    },
    features: {
      title: 'Funktionen — STILGIA',
      description: 'Digitaler KI-Kleiderschrank, virtuelle Anproben, KI-Stylist, Outfit-Kalender, Wetter-Tipps und Travel: Reiseplaner mit smartem Gepäck.',
    },
    pricing: {
      title: 'Preise — STILGIA',
      description: 'Dauerhaft kostenloser Free-Tarif, Premium und Premium Plus. Keine Bindung. Jederzeit über deinen App-Store kündbar.',
    },
    privacy: {
      title: 'Datenschutzerklärung — STILGIA',
      description: 'Wie STILGIA deine personenbezogenen Daten erhebt, verwendet und schützt. DSGVO-konform — wir speichern nur das Notwendige.',
    },
    terms: {
      title: 'Nutzungsbedingungen — STILGIA',
      description: 'STILGIA-Nutzungsbedingungen, Abonnement-Richtlinien, Widerrufsrecht und Haftungsbeschränkungen.',
    },
    help: {
      title: 'Hilfe-Center — STILGIA',
      description: 'Häufige Fragen zu STILGIA: Avatar erstellen, Kleidung hochladen, Outfits generieren, Abo verwalten und mehr.',
    },
    download: {
      title: 'STILGIA laden — App Store & Google Play',
      description: 'Lade STILGIA kostenlos: dein digitaler KI-Kleiderschrank mit virtueller Anprobe und Outfits. Im App Store und bei Google Play.',
    },
    shop: {
      title: 'STILGIA Shop — Katalog mit virtueller Anprobe',
      description: 'Kuratierter Katalog von Partnermarken mit virtueller Anprobe an deinem Avatar. Kauf über shop.stilgia.com (Shopify). Versand nur nach Deutschland.',
    },
    contact: {
      title: 'Kontakt — STILGIA',
      description: `Brauchst du Hilfe? Schreib uns an ${SUPPORT_EMAIL}. Wir antworten innerhalb von 24 Stunden an Werktagen.`,
    },
  },
};

export function pageSeo(page: WwwPage, locale: WwwLocale): { title: string; description: string } {
  return COPY[locale]?.[page] ?? COPY.es[page];
}

export function pageUrl(page: WwwPage): string {
  // /home is the home page but we also accept the bare root path. We canonicalise to /home.
  // The download page canonicalises to the Spanish route (alias: /download).
  if (page === 'download') return `${SITE_URL}/descargar`;
  return `${SITE_URL}/${page}`;
}
